import { useState, useEffect, useRef } from 'react';

interface UseDeferredRenderOptions {
  /** Delay before rendering in ms. Defaults to 2000ms on mobile, 500ms on desktop */
  delay?: number;
  /** Root margin for IntersectionObserver. Defaults to '200px' */
  rootMargin?: string;
  /** Whether to use IntersectionObserver. Defaults to true */
  useIntersectionObserver?: boolean;
}

/**
 * Hook for deferred rendering with IntersectionObserver support
 * Optimizes performance by delaying render until element is near viewport
 */
export const useDeferredRender = (options: UseDeferredRenderOptions = {}) => {
  const {
    delay,
    rootMargin = '200px',
    useIntersectionObserver = true
  } = options;

  const [shouldRender, setShouldRender] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(!useIntersectionObserver);
  const elementRef = useRef<HTMLDivElement>(null);

  // Determine delay based on device
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const actualDelay = delay ?? (isMobile ? 2000 : 500);

  // Timeout-based deferred render
  useEffect(() => {
    const timeoutId = setTimeout(() => setShouldRender(true), actualDelay);
    return () => clearTimeout(timeoutId);
  }, [actualDelay]);

  // IntersectionObserver-based render
  useEffect(() => {
    if (!useIntersectionObserver || !elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsIntersecting(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold: 0.01
      }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [rootMargin, useIntersectionObserver]);

  // Render only when both conditions are met
  const canRender = shouldRender && isIntersecting;

  return {
    canRender,
    elementRef,
    isMobile
  };
};
